import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Camera, OrthographicCamera, WebGLRenderer } from 'three';

import { isDefined } from '../../../../utils/variables';
import { useObject3d } from '../../../hooks/three/use-object-3d';
import { useOrbitControls } from '../../../hooks/three/use-orbit-controls';
import { useOrbitControlsCursorFocusPoint } from '../../../hooks/three/use-orbit-controls-cursor-focus-point';
import { useWms } from '../../../hooks/three/use-wms';
import { useDialog } from '../../../hooks/use-dialog';
import { useElementWidth } from '../../../hooks/use-element-width';
import { usePreventPageUnload } from '../../../hooks/use-prevent-page-unload';
import { useAppContext } from '../../../state/app-state';
import { reset } from '../../../utils/styled-reset';
import { InfoBox } from '../../info-box/info-box';
import { ToolIcon } from '../../skyview-new/toolbar/tool-icon';
import { ToolbarGroup } from '../../skyview-new/toolbar/toolbar-group';
import { Stack } from '../../stack/stack';
import { GeodataSettingsDialog } from '../geodata-configuration/geodata-settings-dialog';
import { GeodataImagesContext, GeodataImagesState } from '../geodata-images-state';
import { GeodataContext, GeodataState } from '../geodata-state';
import { useViewerBoundaryPolygons } from '../use-viewer-boundary-polygons';
import { AlignerState } from './aligner-state';
import {
  GeodataSpatialReferenceContext,
  GeodataSpatialReferenceState,
} from './geodata-spatial-reference-state';
import { GeodataStatusIcon } from './geodata-status-icon';
import { IntroductionDialog } from './introduction-dialog';
import { sfmViewerLayering } from './layering';
import { PlaceMarkersContext, PlaceMarkersState } from './place-markers-state';
import { PlaceMarkerViewer } from './place-markers-viewer';
import { ReviewGcpMarkerEstimatesDialog } from './review-gcp-marker-estimates-dialog';
import { SfmViewerRightMenu } from './sfm-viewer-right-menu';
import { SfmViewerTools } from './sfm-viewer-tools';
import { SfmViewerTool } from './sfm-viewer-tooltip';
import { useCenterCameraCallback } from './use-center-camera-callback';
import { useCursor } from './use-cursor';
import { useGcpPoints } from './use-gcp-points';
import { useGeodataCameras } from './use-geodata-cameras';
import { useOrthoPreview } from './use-ortho-preview';
import { useSfmRenderingContext } from './use-sfm-rendering-context';
import { useSparseReconstruction } from './use-sparse-reconstruction';

/**
 * SfM viewer (Structure from motion).
 *
 * Works as an interface towards the SfM step in photogrammetry which is an intermediate state
 * where the camera poses and sparse reconstruction can be refined before moving on to dense
 * reconstruction.
 */
type SfmViewerProps = {
  geodataId?: string;
};

export const SfmViewer = ({ geodataId }: SfmViewerProps = {}) => {
  if (!geodataId) {
    return (
      <InfoBox color="red" leftIcon={{ icon: ['fad', 'exclamation-triangle'] }}>
        No geodata was specified for GCP.
      </InfoBox>
    );
  }

  return (
    <GeodataState geodataId={geodataId}>
      <GeodataImagesState>
        <GeodataSpatialReferenceState>
          <PlaceMarkersState>
            <AlignerState>
              <SfmViewerContent />
            </AlignerState>
          </PlaceMarkersState>
        </GeodataSpatialReferenceState>
      </GeodataImagesState>
    </GeodataState>
  );
};

/**
 * Reflects the geodata upload progress as the app-wide "unsaved changes" flag
 * (which guards page unload and in-app navigation). Kept as a separate,
 * null-rendering component so the SfM viewer's heavy three.js render tree is not
 * coupled to upload-progress changes (SK-7199).
 */
const UnsavedChangesTracker = () => {
  const { progress } = useContext(GeodataImagesContext);
  const { setPageHasUnsavedChanges } = useAppContext();

  usePreventPageUnload();

  useEffect(() => {
    setPageHasUnsavedChanges(progress.state === 'pending');
    return () => {
      setPageHasUnsavedChanges(false);
    };
  }, [progress.state, setPageHasUnsavedChanges]);

  return null;
};

const SfmViewerContent = () => {
  const { t } = useTranslation();
  const webGlContainer = useRef<HTMLDivElement>(null);
  const { geodata } = useContext(GeodataContext);
  const { showPlaceMarkersDialog } = useContext(PlaceMarkersContext);
  const { boundingBox } = useContext(GeodataSpatialReferenceContext);

  const settingsDialog = useDialog();
  const placeMarkersDialog = useDialog();
  const reviewGcpMarkerEstimatesDialog = useDialog();

  const onBeforeRenderRef = useRef<(camera: Camera, renderer: WebGLRenderer) => void>();
  const { webGlScene, camera, setCameraType } = useSfmRenderingContext(webGlContainer, {
    onBeforeRenderRef,
  });

  const { cameraGroup } = useGeodataCameras();
  const { sparsePointCloud } = useSparseReconstruction();
  const { orthoPreview } = useOrthoPreview();
  const { gcp } = useGcpPoints();
  const { boundaryPolygonsGroup } = useViewerBoundaryPolygons(geodata.id);
  const [showPreviews, setShowPreviews] = useState(true);
  const [showWms, setShowWms] = useState(false);
  const [currentTool, setCurrentTool] = useState<SfmViewerTool>('select');
  const controls = useOrbitControls(webGlContainer, camera);

  const isOrthoView = useMemo(() => camera instanceof OrthographicCamera, [camera]);
  const { wmsQuad } = useWms(webGlContainer, controls, showWms && isOrthoView, onBeforeRenderRef);

  useObject3d(webGlScene, wmsQuad);
  useObject3d(webGlScene, isOrthoView ? orthoPreview : undefined);
  useObject3d(webGlScene, sparsePointCloud);
  useObject3d(webGlScene, gcp?.points);
  useObject3d(webGlScene, gcp?.labels);
  useObject3d(webGlScene, cameraGroup);
  useObject3d(webGlScene, boundaryPolygonsGroup);

  const { intersection, cursorPosition } = useCursor(
    webGlContainer,
    webGlScene,
    camera,
    gcp?.points,
    cameraGroup,
  );

  useOrbitControlsCursorFocusPoint(controls, cursorPosition, camera, webGlContainer);

  useEffect(() => {
    if (showPlaceMarkersDialog) {
      placeMarkersDialog.show();
    } else {
      placeMarkersDialog.hide();
    }
  }, [showPlaceMarkersDialog, placeMarkersDialog]);

  const isCenterEnabled = useMemo(
    () => isDefined(camera) && isDefined(controls) && isDefined(boundingBox),
    [camera, controls, boundingBox],
  );

  const centerCamera = useCenterCameraCallback(controls, camera);
  const { ref: stackRef, width: stackWidth } = useElementWidth();

  return (
    <Component>
      <UnsavedChangesTracker />
      <StyledStack direction="row" ref={stackRef} spacing={0}>
        <Viewer>
          <WebGlCanvasContainer ref={webGlContainer} showWms={showWms && isOrthoView} />

          <SfmViewerTools
            cameraGroup={cameraGroup}
            currentTool={currentTool}
            cursorPosition={cursorPosition}
            gcpObjectUuid={gcp?.points.uuid}
            intersection={intersection}
            pointerRef={webGlContainer}
            webGlScene={webGlScene}
            onToolChange={setCurrentTool}
          />

          <Toolbar direction="row" spacing={0.2}>
            <Positioned>
              <ToolbarGroup>
                <ToolIcon
                  active={isOrthoView}
                  callback={() => setCameraType('orthographic')}
                  icon={['fal', 'image']}
                  tooltip={{
                    title: t('orthophoto.tooltip.title', { ns: 'skyviewToolbar' }),
                    text: t('orthophoto.tooltip.text', { ns: 'skyviewToolbar' }),
                  }}
                />
                <ToolIcon
                  active={!isOrthoView}
                  callback={() => setCameraType('perspective')}
                  icon={['fal', 'cube']}
                  tooltip={{
                    title: t('perspective.tooltip.title', { ns: 'skyviewToolbar' }),
                    text: t('perspective.tooltip.text', { ns: 'skyviewToolbar' }),
                  }}
                />
              </ToolbarGroup>

              <ToolbarGroup>
                <ToolIcon
                  active={currentTool === 'select'}
                  callback={() => setCurrentTool('select')}
                  icon={['fal', 'hand']}
                  tooltip={{
                    title: t('gcpPage.selectTool.tooltip.title', { ns: 'cloudProcessing' }),
                    text: t('gcpPage.selectTool.tooltip.text', { ns: 'cloudProcessing' }),
                  }}
                />
                <ToolIcon
                  active={currentTool === 'measure'}
                  callback={() => setCurrentTool('measure')}
                  icon={['fal', 'ruler']}
                  tooltip={{
                    title: t('gcpPage.measureTool.tooltip.title', { ns: 'cloudProcessing' }),
                    text: t('gcpPage.measureTool.tooltip.text', { ns: 'cloudProcessing' }),
                  }}
                />
              </ToolbarGroup>

              <ToolbarGroup>
                <ToolIcon
                  active={showPreviews && isDefined(sparsePointCloud)}
                  callback={() =>
                    setShowPreviews((oldState) => {
                      if (sparsePointCloud) {
                        sparsePointCloud.visible = !oldState;
                      }
                      if (orthoPreview) {
                        orthoPreview.visible = !oldState;
                      }

                      return !oldState;
                    })
                  }
                  disabled={!isDefined(sparsePointCloud)}
                  icon={['fal', 'magnifying-glass']}
                  tooltip={{
                    title: t('aligner.preview', { ns: 'cloudProcessing' }),
                    text: t('aligner.previewInfo', { ns: 'cloudProcessing' }),
                  }}
                />

                <ToolIcon
                  active={false}
                  callback={() => centerCamera()}
                  disabled={!isCenterEnabled}
                  icon={['fal', 'expand']}
                  tooltip={{
                    title: t('focus.tooltip.title', { ns: 'skyviewToolbar' }),
                    text: t('focus.tooltip.text', { ns: 'skyviewToolbar' }),
                  }}
                />

                <ToolIcon
                  active={showWms}
                  callback={() => {
                    setShowWms((oldState) => {
                      return !oldState;
                    });
                  }}
                  disabled={!isOrthoView}
                  icon={['fal', 'map']}
                  tooltip={{
                    title: t('wms.tooltip.title', { ns: 'skyviewToolbar' }),
                    text: t('wms.tooltip.text', { ns: 'skyviewToolbar' }),
                  }}
                />

                <ToolIcon
                  active={false}
                  callback={() => reviewGcpMarkerEstimatesDialog.show()}
                  icon={['fal', 'clipboard-check']}
                  tooltip={{
                    title: t('reviewGcpMarkerEstimatesDialog.header', { ns: 'cloudProcessing' }),
                    text: t('reviewGcpMarkerEstimatesDialog.info', { ns: 'cloudProcessing' }),
                  }}
                />

                <ToolIcon
                  active={false}
                  callback={() => settingsDialog.show()}
                  icon={['fad', 'cog']}
                  tooltip={{
                    title: t('settings.title', { ns: 'cloudProcessing' }),
                    text: t('settings.title', { ns: 'cloudProcessing' }),
                  }}
                />
              </ToolbarGroup>
            </Positioned>
          </Toolbar>

          {placeMarkersDialog.visible && (
            <PlaceMarkerContainer>
              <PlaceMarkerViewer />
            </PlaceMarkerContainer>
          )}
        </Viewer>

        <SfmViewerRightMenu parentWidth={stackWidth} />

        <GeodataStatusIcon showWms={showWms} />
      </StyledStack>
      <IntroductionDialog />
      {settingsDialog.render(<GeodataSettingsDialog dialog={settingsDialog} />)}
      {reviewGcpMarkerEstimatesDialog.render(
        <ReviewGcpMarkerEstimatesDialog dialog={reviewGcpMarkerEstimatesDialog} />,
      )}
    </Component>
  );
};

const StyledStack = styled(Stack)`
  position: relative;
  height: 100%;
`;

const Toolbar = styled(Stack)`
  align-items: center;
  justify-content: center;
`;

const Positioned = styled.div`
  position: absolute;
  bottom: 16px;

  display: flex;
  flex-wrap: wrap;
  border-radius: 5px;

  margin: 0 16px;
  padding: 8px;

  background-color: #fff;
  box-shadow:
    0 3px 6px rgba(0, 0, 0, 0.16),
    0 3px 6px rgba(0, 0, 0, 0.23);

  z-index: ${sfmViewerLayering.menu};
`;

const Component = styled.div`
  ${reset}
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const Viewer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const WebGlCanvasContainer = styled.div<{ showWms: boolean }>`
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: ${(props) => (props.showWms ? '#B0D19E' : '#000000')};
  z-index: ${sfmViewerLayering.webGlCanvasContainer};
`;

const PlaceMarkerContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: ${sfmViewerLayering.placeMarkersContainer};
`;
